(() => {
  const OFFER_SOURCE = 'plaqueo-ebook-offer';
  const STATE_STORAGE_KEY = 'plaqueo_ebook_offer_units';
  const VIEW_STORAGE_PREFIX = 'plaqueo_event_seen_';
  let syncing = false;
  let syncQueued = false;

  const emit = (name, details = {}) => {
    const payload = {
      ...details,
      event_source: 'plaqueo_theme',
      timestamp: new Date().toISOString(),
    };

    try {
      if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(name, payload);
    } catch (error) {
      console.warn(`[Plaqueo] Analytics Shopify indisponible pour ${name}.`, error);
    }

    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, payload);
    } catch (error) {
      console.warn(`[Plaqueo] Analytics Google indisponible pour ${name}.`, error);
    }
  };

  const getStateElement = () =>
    document.querySelector('main .plaqueo-ebook-offer-data') ||
    document.querySelector('#CartDrawer .plaqueo-ebook-offer-data') ||
    document.querySelector('.plaqueo-ebook-offer-data');

  const readState = () => {
    const element = getStateElement();
    if (!element) return null;

    return {
      enabled: element.dataset.enabled === 'true',
      threshold: Number(element.dataset.threshold || 3),
      plaqueUnits: Number(element.dataset.plaqueUnits || 0),
      ebookVariantId: Number(element.dataset.ebookVariantId || 0),
      hasPaidEbook: element.dataset.hasPaidEbook === 'true',
      gifts: [...element.querySelectorAll('[data-plaqueo-gift-line]')].map((line) => ({
        key: line.dataset.key,
        quantity: Number(line.dataset.quantity || 0),
      })),
    };
  };

  const sectionIds = () => {
    const ids = ['cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('main-cart-items')) ids.push('main-cart-items');
    if (document.getElementById('main-cart-footer')) ids.push('main-cart-footer');
    if (document.getElementById('cart-live-region-text')) ids.push('cart-live-region-text');
    return ids;
  };

  const getSource = (html, selector) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  };

  const renderSections = (sections = {}) => {
    const drawerTarget = document.getElementById('CartDrawer');
    const drawerSource = getSource(sections['cart-drawer'], '#CartDrawer');
    if (drawerTarget && drawerSource) {
      drawerTarget.innerHTML = drawerSource.innerHTML;
      const drawer = drawerTarget.closest('cart-drawer');
      const overlay = drawerTarget.querySelector('#CartDrawer-Overlay');
      if (drawer && overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
    }

    const iconTarget = document.getElementById('cart-icon-bubble');
    const iconSource = getSource(sections['cart-icon-bubble'], '#cart-icon-bubble');
    if (iconTarget && iconSource) iconTarget.innerHTML = iconSource.innerHTML;

    const cartItemsTarget = document.querySelector('#main-cart-items .js-contents');
    const cartItemsSource = getSource(sections['main-cart-items'], '#main-cart-items .js-contents');
    if (cartItemsTarget && cartItemsSource) cartItemsTarget.innerHTML = cartItemsSource.innerHTML;

    const offerTarget = document.getElementById('PlaqueoOfferCart');
    const offerSource = getSource(sections['main-cart-items'], '#PlaqueoOfferCart');
    if (offerTarget && offerSource) offerTarget.innerHTML = offerSource.innerHTML;

    const cartFooterTarget = document.querySelector('#main-cart-footer .js-contents');
    const cartFooterSource = getSource(sections['main-cart-footer'], '#main-cart-footer .js-contents');
    if (cartFooterTarget && cartFooterSource) cartFooterTarget.innerHTML = cartFooterSource.innerHTML;

    const liveTarget = document.getElementById('cart-live-region-text');
    const liveSource = getSource(sections['cart-live-region-text'], '#cart-live-region-text');
    if (liveTarget && liveSource) liveTarget.innerHTML = liveSource.innerHTML;
  };

  const request = async (path, body) => {
    const response = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        ...body,
        sections: sectionIds(),
        sections_url: window.location.pathname,
      }),
    });

    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Erreur panier');
    renderSections(data.sections);
    return data;
  };

  const updateProgressAnalytics = (state) => {
    let previousUnits = 0;
    try {
      previousUnits = Number(sessionStorage.getItem(STATE_STORAGE_KEY) || 0);
      sessionStorage.setItem(STATE_STORAGE_KEY, String(state.plaqueUnits));
    } catch (error) {
      previousUnits = 0;
    }

    if (previousUnits < state.threshold && state.plaqueUnits >= state.threshold) {
      emit('ebook_offer_unlocked', {
        plaque_units: state.plaqueUnits,
        threshold: state.threshold,
      });
    }

    const viewKey = `${VIEW_STORAGE_PREFIX}ebook_offer_progress_view_${state.plaqueUnits}`;
    try {
      if (!sessionStorage.getItem(viewKey)) {
        sessionStorage.setItem(viewKey, '1');
        emit('ebook_offer_progress_view', {
          plaque_units: state.plaqueUnits,
          threshold: state.threshold,
        });
      }
    } catch (error) {
      emit('ebook_offer_progress_view', {
        plaque_units: state.plaqueUnits,
        threshold: state.threshold,
      });
    }
  };

  const sync = async () => {
    if (syncing) {
      syncQueued = true;
      return;
    }

    const state = readState();
    if (!state || !state.enabled) return;
    updateProgressAnalytics(state);
    if (!state.ebookVariantId) {
      console.error('[Plaqueo] ID de variante ebook manquant.');
      return;
    }

    syncing = true;
    try {
      const action = window.PlaqueoEbookOfferCore?.plan(state);
      if (!action) throw new Error('Moteur de décision indisponible');

      if (action.type === 'add') {
        await request(window.routes?.cart_add_url || '/cart/add.js', {
          items: [
            {
              id: state.ebookVariantId,
              quantity: 1,
              properties: {
                _plaqueo_ebook_gift: 'true',
              },
            },
          ],
        });
        emit('ebook_auto_added', {
          ebook_variant_id: state.ebookVariantId,
          plaque_units: state.plaqueUnits,
        });
        emit('ebook_add_to_cart', {
          ebook_variant_id: state.ebookVariantId,
          source: 'automatic_offer',
        });
        return;
      }

      if (action.type === 'update') {
        await request(window.routes?.cart_update_url || '/cart/update.js', { updates: action.updates });
        if (action.removalReason === 'threshold_lost') {
          emit('ebook_removed_after_threshold_lost', {
            ebook_variant_id: state.ebookVariantId,
            plaque_units: state.plaqueUnits,
          });
        }
      }
    } catch (error) {
      console.error('[Plaqueo] Synchronisation de l’offre ebook impossible.', error);
    } finally {
      syncing = false;
      if (syncQueued) {
        syncQueued = false;
        window.setTimeout(sync, 0);
      }
    }
  };

  const scheduleSync = () => window.setTimeout(sync, 80);

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-plaqueo-analytics]');
    if (!target) return;
    emit(target.dataset.plaqueoAnalytics, {
      link_url: target.href || '',
    });
  });

  const trackSectionViews = () => {
    const sections = [...document.querySelectorAll('[data-plaqueo-view-analytics]')];
    if (!sections.length) return;

    const reportView = (section) => {
      if (section.dataset.plaqueoViewTracked === 'true') return;
      section.dataset.plaqueoViewTracked = 'true';
      emit(section.dataset.plaqueoViewAnalytics, {
        section_id: section.id || section.closest('.shopify-section')?.id || '',
      });
    };

    if (!('IntersectionObserver' in window)) {
      sections.forEach(reportView);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reportView(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 },
    );

    sections.forEach((section) => observer.observe(section));
  };

  document.addEventListener('DOMContentLoaded', () => {
    const tracking = window.plaqueoEbookTracking || {};
    trackSectionViews();

    if (tracking.isEbookProduct) {
      emit('ebook_product_view', {
        ebook_variant_id: Number(tracking.ebookVariantId || 0),
        product_handle: 'le-kit-avis-google-guide-pratique-pdf',
      });
    }

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (
          Number(event.productVariantId || event.variantId || 0) === Number(tracking.ebookVariantId || 0) &&
          event.source !== OFFER_SOURCE
        ) {
          emit('ebook_add_to_cart', {
            ebook_variant_id: Number(tracking.ebookVariantId || 0),
            source: event.source || 'theme',
          });
        }
        scheduleSync();
      });
    }

    scheduleSync();
  });
})();
